/**
 * The install command, and the live card that says whether it worked.
 *
 * **The command is shown with a placeholder, not with a token in it.** The design shows a
 * ready-to-paste command and captions it "your token is already filled in", and that is not
 * something this product can do: a token's value exists in exactly one HTTP response and only a
 * digest is kept afterwards. Athena cannot read your token back to you any more than it can read
 * your password back. So the command carries `YOUR_TOKEN` and the screen sends you to Tokens to
 * mint one, where the reveal dialog is the place to copy it from.
 *
 * This is also where a brand-new account lands, which the current design export decided and this
 * app follows: the first thing a new workspace needs is a connected assistant.
 *
 * **Two halves, one per step.** The MCP server answers questions; the skill is what makes the agent
 * ask them. The skill is published to Athena's skills registry like any other and installed with
 * APM, which a new user does not have yet — and the guide to installing APM is itself served by
 * Athena. So step 2 is a prompt, not a command: pasted into the agent connected in step 1, it has
 * that agent read the APM guide through the MCP server, install APM, and install the skill into
 * the repository it is working in. The agent needs nothing beforehand except the connection.
 *
 * The live card is `GET /api/v1/status`, polled. The server decides the state and returns it as one
 * field, because two clients combining booleans differently is how a status display starts lying.
 *
 * **Two cards, not three.** `StatusResponse.state` still has three values, and the export draws
 * two: waiting, and connected — which is what `answering` renders as here. The export's third card
 * is gone, and with it the line that read "2 assistants connected", a number nothing ever produced
 * (`call_count` counts calls, not callers). `StatusResponse.note` therefore has no reader in this
 * app; the state it names is still reached, it just does not get its own card.
 */

import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { CopyButton } from "../components/copy-button";
import { ErrorNotice, Loading } from "../components/feedback";
import * as workspaceApi from "../lib/api/workspace-api";
import { relativeTime } from "../lib/ui/format";
import { LIVE_POLL_MS, usePollTick } from "../lib/ui/use-poll";
import { useResource } from "../lib/ui/use-resource";

const TOKEN_PLACEHOLDER = "YOUR_TOKEN";

/** The skill as APM names it. The range follows the major version in `skills/athena/apm.yml`. */
const ATHENA_SKILL = "athena/athena#^1.0.0";

/**
 * What to paste into the agent connected in step 1. It spells every step out, unlike the one-line
 * prompts on the Skills page: those rely on the athena skill to know what to do, and this is the
 * prompt that installs it.
 *
 * Into the repository, like every skill from the registry: `apm.yml` makes Athena the default and
 * `apm.lock.yaml` pins the digest, so everyone who clones it installs the same bytes with
 * `apm install --frozen`. The installed folder is ignored, never committed: APM's record of what it
 * deployed is not in git, so a clone's `apm install` deletes committed skill files.
 *
 * **The token is the one step 1 already configured.** APM needs the same `ath_` token the MCP
 * connection sends, so the agent reads it out of that client's MCP config once and saves it in the
 * shell profile as `ATHENA_TOKEN` (what template downloads and the guides read) and
 * `APM_REGISTRY_TOKEN_ATHENA` (what APM reads). Every later session and `apm install` then has it,
 * and nobody types it. It is read and written in one command, so it is never shown in the chat.
 */
function skillPrompt(
  agent: string,
  target: string,
  skillDir: string,
  tokenSource: string,
): string {
  const registry = registryUrl();
  return `Install the Athena skill into this repository for ${agent}.

You are connected to Athena as an MCP server, but the skill that tells you when to call its tools is not installed yet. Install it from Athena's skills registry with APM:

1. Call Athena's athena_guide tool with "install APM and set it up for Athena's skills registry", and follow it: install the APM CLI if it is missing, turn registries on, and bind the registry name "athena" to ${registry}.

2. Call athena_guide with "install a skill from Athena's registry into a repository with APM", and follow it to make Athena the default registry in apm.yml at the root of this repository, with the URL ${registry}, and to list ${target} under targets:.

3. Save the token, once. APM needs the same token your Athena MCP connection uses: it is the Authorization header of the "athena" server in ${tokenSource}, after "Bearer ". If ATHENA_TOKEN is not already set, read it from there and append these two lines to my shell profile (~/.zshrc for zsh, ~/.bashrc for bash), so every later session and command has it:
export ATHENA_TOKEN="<the token>"
export APM_REGISTRY_TOKEN_ATHENA="$ATHENA_TOKEN"
Read and write it in one command, so the value is never printed or shown in this chat. Do not ask me for it.

4. Install the skill, loading those two lines first if your shell does not have them yet:
eval "$(grep -E '^export (ATHENA_TOKEN|APM_REGISTRY_TOKEN_ATHENA)=' ~/.zshrc)" && apm install '${ATHENA_SKILL}' --target ${target}
(Use ~/.bashrc instead if that is where you wrote them.)

5. Check that ${skillDir}/athena/SKILL.md exists. Add ${skillDir}/athena/ to .gitignore next to apm_modules/: the installed skill is never committed. Show me apm.yml, apm.lock.yaml and .gitignore to commit, then tell me to start a new session so the skill loads.

Ask me before you install any software.`;
}

const CLIENTS = [
  {
    id: "claude",
    label: "Claude Code",
    caption: "Claude Code · terminal",
    command: `claude mcp add --scope user --transport http athena ${mcpUrl()} --header "Authorization: Bearer ${TOKEN_PLACEHOLDER}"`,
    skillCaption: "Claude Code · prompt · .claude/skills/athena",
    skillPrompt: skillPrompt(
      "Claude Code",
      "claude",
      ".claude/skills",
      '~/.claude.json (under "mcpServers"; an older install may have it under this repository\'s absolute path in "projects")',
    ),
  },
  {
    id: "cursor",
    label: "Cursor",
    caption: "Cursor · ~/.cursor/mcp.json",
    command: `{
  "mcpServers": {
    "athena": {
      "url": "${mcpUrl()}",
      "headers": { "Authorization": "Bearer ${TOKEN_PLACEHOLDER}" }
    }
  }
}`,
    skillCaption: "Cursor · prompt · .agents/skills/athena",
    skillPrompt: skillPrompt(
      "Cursor",
      "cursor",
      ".agents/skills",
      "~/.cursor/mcp.json (or .cursor/mcp.json in this repository)",
    ),
  },
  {
    id: "codex",
    label: "Codex",
    caption: "Codex · ~/.codex/config.toml",
    command: `[mcp_servers.athena]
url = "${mcpUrl()}"
http_headers = { Authorization = "Bearer ${TOKEN_PLACEHOLDER}" }`,
    skillCaption: "Codex · prompt · .agents/skills/athena",
    skillPrompt: skillPrompt(
      "Codex",
      "codex",
      ".agents/skills",
      "~/.codex/config.toml (http_headers under [mcp_servers.athena])",
    ),
  },
  {
    id: "other",
    label: "Other",
    caption: "Any MCP client · streamable HTTP",
    command: `${mcpUrl()}
Authorization: Bearer ${TOKEN_PLACEHOLDER}`,
    skillCaption: "Agent Skills standard · prompt · .agents/skills/athena",
    skillPrompt: skillPrompt(
      "this coding agent",
      "agent-skills",
      ".agents/skills",
      "your MCP client's configuration",
    ),
  },
] as const;

/**
 * The MCP endpoint, as an absolute URL somebody can paste into a config file on their laptop.
 *
 * **Configuration, not inference, and that is the whole point.** The first version of this derived
 * the origin from `VITE_API_BASE_URL`, which is the relative path `/api/v1` in development — so it
 * resolved against `location.origin` and printed the *dashboard's* hostname. The command looked
 * entirely plausible and pointed at a server with no `/mcp` on it. It was caught by reading the
 * command on the screen, not by a test, because every test that could have caught it would have
 * had to know the right answer already.
 *
 * So the deployed URL is supplied explicitly at build time. It is public by construction — it is
 * the address of a public endpoint — and it is emphatically not a credential.
 *
 * The fallbacks are ordered by how much they can be trusted:
 *   1. `VITE_MCP_URL`, which somebody set deliberately.
 *   2. The API base, when it is an absolute URL — then its origin is genuinely the API's.
 *   3. The current origin, which is correct only for a same-origin deployment.
 */
function mcpUrl(): string {
  const explicit = import.meta.env.VITE_MCP_URL;
  if (typeof explicit === "string" && explicit.length > 0) return explicit.replace(/\/$/, "");

  const base = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  try {
    if (/^https?:\/\//.test(base)) return `${new URL(base).origin}/mcp`;
    return new URL("/mcp", globalThis.location.origin).href;
  } catch {
    return "/mcp";
  }
}

/** Athena's skills registry. It is served by the same service as the MCP endpoint. */
function registryUrl(): string {
  return mcpUrl().replace(/\/mcp$/, "/registry");
}

export function InstallScreen() {
  const [client, setClient] = useState<(typeof CLIENTS)[number]["id"]>("claude");

  const load = useCallback((signal: AbortSignal) => workspaceApi.getStatus(signal), []);
  // Polls until an assistant has actually connected. After that the card is describing something
  // that already happened and there is nothing left to discover.
  const status = useResource(load, [usePollTick(LIVE_POLL_MS)]);

  const active = CLIENTS.find((entry) => entry.id === client) ?? CLIENTS[0];

  return (
    <>
      <h1 className="page-title">Install</h1>
      <p className="page-lede">Connect an assistant, then give it the skill that makes it ask.</p>

      <div className="tabs" role="tablist" aria-label="Client">
        {CLIENTS.map((entry) => (
          <button
            key={entry.id}
            className="tab"
            type="button"
            role="tab"
            aria-selected={client === entry.id}
            onClick={() => {
              setClient(entry.id);
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <h2 className="section-title">1. Connect the server</h2>
      <div className="command-card">
        <div className="command-card__head">
          <span className="command-card__caption">{active.caption}</span>
          <CopyButton value={active.command} className="command-card__copy" />
        </div>
        <pre className="command-card__body">{active.command}</pre>
      </div>

      <p className="page-footnote">
        Replace <span className="mono">{TOKEN_PLACEHOLDER}</span> with a token.{" "}
        <Link to="/tokens">Create one on the Tokens screen</Link> — its value is shown once, in the
        dialog that appears the moment it is created, and never again. Athena keeps only a
        fingerprint of it, so there is no way to pre-fill this command for you.
      </p>

      <h2 className="section-title">2. Add the skill</h2>
      <div className="command-card">
        <div className="command-card__head">
          <span className="command-card__caption">{active.skillCaption}</span>
          <CopyButton value={active.skillPrompt} className="command-card__copy" />
        </div>
        <pre className="command-card__body">{active.skillPrompt}</pre>
      </div>

      <p className="page-footnote">
        Paste it into the assistant you connected in step 1, at the root of your repository. It
        reads how to install APM from Athena, then installs the skill into the repository.
        Commit <span className="mono">apm.yml</span> and{" "}
        <span className="mono">apm.lock.yaml</span>, and everyone who clones it gets the same
        skill with <span className="mono">apm install --frozen</span>. The assistant saves the
        token from step 1 in your shell profile, so you never set it by hand.
        {active.id === "other" &&
          " Most clients that support Agent Skills read .agents/skills; if yours reads skills from somewhere else, tell the assistant which folder."}
      </p>

      <h2 className="section-title">Is it working?</h2>
      {status.loading && <Loading what="your connection status" />}
      {status.error !== null && <ErrorNotice error={status.error} onRetry={status.reload} />}
      {status.data !== null && (
        <div className="status-card" data-state={status.data.state}>
          {status.data.state === "waiting" && (
            <>
              <p className="status-card__headline">
                Waiting. No assistant has connected with this workspace yet.
              </p>
              {/* The export's wording is "with this token". A token is not what this call is
                  scoped to — `GET /status` resolves a workspace from whichever credential asked,
                  so a second token connecting would clear this too. */}
              <p className="status-card__body">
                This updates by itself the moment one does — no refresh needed. Leave this open
                while you paste the command.
              </p>
            </>
          )}
          {status.data.state !== "waiting" && (
            <>
              <p className="status-card__headline">
                Connected. The Handbook has been searched {status.data.handbook_search_count}{" "}
                time{status.data.handbook_search_count === 1 ? "" : "s"}.
              </p>
              <p className="status-card__body">
                Next: publish something. The moment your assistant uses it, you will see it here.
              </p>
            </>
          )}
          <p className="status-card__meta">
            {status.data.call_count} call{status.data.call_count === 1 ? "" : "s"} ·{" "}
            {status.data.handbook_search_count} handbook search
            {status.data.handbook_search_count === 1 ? "" : "es"}
            {status.data.first_seen_at !== null && (
              <> · first seen {relativeTime(status.data.first_seen_at)}</>
            )}
          </p>
          <p className="status-card__lag">
            These counters can be up to {status.data.counter_lag_seconds} seconds behind. Athena
            does not name which assistant connected — it records that one did, and when.
          </p>
        </div>
      )}
    </>
  );
}
